/**
 * The admin UI's accent for an operational tenant: the `branding.primaryColor`
 * the tenant configures under Konto → Branding, the same value its booking page
 * uses (ADR-0024). Legacy tenants keep their bundled colour in `App.tsx`.
 */

const TRIPLET = /^(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})$/;

/** "0, 84, 117" → [0, 84, 117]; `undefined` for anything that isn't a valid
 *  RGB triplet, so an unset or malformed colour never becomes black. */
export const parseRgbTriplet = (value: string | null | undefined): [number, number, number] | undefined => {
    const match = value?.trim().match(TRIPLET);
    if (!match) return undefined;
    const rgb = [match[1], match[2], match[3]].map(Number);
    if (rgb.some((c) => c > 255)) return undefined;
    return [rgb[0], rgb[1], rgb[2]];
};

/** "#005475" → "0, 84, 117" (the stored shape); `undefined` if not "#rrggbb". */
export const hexToTriplet = (hex: string): string | undefined => {
    const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
    return match ? [match[1], match[2], match[3]].map((c) => parseInt(c, 16)).join(', ') : undefined;
};

const linear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** Text colour on the accent, ADR-0024's rule: white when the primary's
 *  relative luminance is ≤ 0.179, black above it. */
export const onPrimaryFor = ([r, g, b]: [number, number, number]): '#ffffff' | '#000000' => {
    const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
    return luminance <= 0.179 ? '#ffffff' : '#000000';
};

/** Apply a tenant's configured accent. Returns false (and changes nothing)
 *  when no valid colour is configured — the default then stays in place. */
export const applyTenantAccent = (primaryColor: string | null | undefined): boolean => {
    const rgb = parseRgbTriplet(primaryColor);
    if (!rgb) return false;
    const style = document.documentElement.style;
    style.setProperty('--color-primary', rgb.join(', '));
    style.setProperty('--color-button-text-contrast', onPrimaryFor(rgb));
    return true;
};
